import { ExpandLessSharp, ExpandMoreSharp } from "@mui/icons-material"
import { Box, Collapse, Typography } from "@mui/material"
import Stack from "@mui/material/Stack"
import React, { useMemo, useState } from "react"

import useToggle from "@hooks/use_toggle"

import styles from "./styles"

export type AccordionProps = {
  title: string
  rowList: AccordionRow[]
  isDefaultOpen: boolean
}

export type AccordionRow = {
  label: string
  value: string
  Icon?: React.ReactNode
  rightIcon?: React.ReactNode
}

export default function AccordionGroup({
  accordionList,
  selectedValue,
  onClick,
  onClickRightIconGroup,
  onClickRightIcon
}: {
  accordionList: {
    title: string
    rightIconGroup?: React.ReactNode
    rowList: AccordionRow[]
    isDefaultOpen: boolean
  }[]
  selectedValue?: string
  onClick?: (value: string) => void
  onClickRightIconGroup?: (value: string) => void
  onClickRightIcon?: (value: string) => void
}) {
  return (
    <Stack width={"100%"} gap={1}>
      {accordionList.map((accordion, index) => (
        <Accordion
          isDefaultOpen={accordion.isDefaultOpen}
          title={accordion.title}
          rowList={accordion.rowList}
          key={`accordion_${index}`}
          selectedValue={selectedValue}
          onClick={onClick}
          onClickRightIconGroup={onClickRightIconGroup}
          onClickRightIcon={onClickRightIcon}
          rightIconGroup={accordion.rightIconGroup}
        />
      ))}
    </Stack>
  )
}

function Accordion({
  rowList,
  title,
  selectedValue,
  onClick,
  onClickRightIconGroup,
  onClickRightIcon,
  isDefaultOpen,
  rightIconGroup
}: {
  rowList: AccordionRow[]
  title: string
  isDefaultOpen: boolean
  selectedValue?: string
  rightIconGroup?: React.ReactNode
  onClick?: (value: string) => void
  onClickRightIconGroup?: (value: string) => void
  onClickRightIcon?: (value: string) => void
}) {
  const [isOpen, toggleIsOpen] = useToggle(
    isDefaultOpen || rowList.some((row) => row.value === selectedValue)
  )
  const formattedListMemo = useMemo(
    () =>
      rowList.map((row) => ({
        ...row,
        isSelected: row.value === selectedValue
      })),
    [rowList, selectedValue]
  )

  const ExpandButton = useMemo(
    () => (isOpen ? ExpandMoreSharp : ExpandLessSharp),
    [isOpen]
  )

  const rightIconGroupMemo = useMemo(() => {
    if (!rightIconGroup) {
      return undefined
    }

    return rightIconGroup
  }, [rightIconGroup])

  function handleClickRow(value: string) {
    onClick?.(value)
    onClickRightIcon?.(value)
  }

  return (
    <Collapse
      in={isOpen}
      collapsedSize={"2rem"}
      sx={{ cursor: "pointer", pr: 1, mt: 0.5 }}
    >
      <Stack
        sx={{ height: "2rem", width: "100%" }}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        px={1}
      >
        <Stack direction={"row"} onClick={toggleIsOpen}>
          <Typography fontWeight={"500"}>{title}</Typography>
          <ExpandButton />
        </Stack>
        <Box mr={-2} onClick={() => onClickRightIconGroup?.(title)}>
          {rightIconGroupMemo}
        </Box>
      </Stack>
      <Stack gap={0.5}>
        {formattedListMemo.map((row, i) => (
          <Row
            {...row}
            key={`${i}_${row.value}`}
            onClick={() => handleClickRow(row.value)}
          />
        ))}
      </Stack>
    </Collapse>
  )
}

function Row({
  isSelected,
  label,
  Icon,
  rightIcon,
  onClick,
  value
}: AccordionRow & { isSelected: boolean; onClick?: (value: string) => void }) {
  const IconMemo = useMemo(() => (Icon ? Icon : <></>), [Icon])
  const RightIconMemo = useMemo(
    () => (rightIcon ? rightIcon : <></>),
    [rightIcon]
  )

  function handleClickRow() {
    onClick?.(value)
  }
  const [showRIcon, setShowRIcon] = useState(false)
  return (
    <Stack
      direction={"row"}
      justifyContent={"space-between"}
      sx={styles.rowContainer(isSelected)}
      onMouseEnter={() => setShowRIcon(true)}
      onMouseLeave={() => setShowRIcon(false)}
    >
      <Stack
        direction={"row"}
        alignItems={"center"}
        gap={1}
        onClick={handleClickRow}
        sx={{ width: "90%" }}
      >
        {IconMemo}
        <Typography sx={styles.rowText}>{label}</Typography>
      </Stack>
      <Box mr={-2} sx={{ display: isSelected || showRIcon ? "block" : "none" }}>
        {RightIconMemo}
      </Box>
    </Stack>
  )
}
